import { Component, inject, signal } from '@angular/core';
import { Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { ShimmerEffect } from '../../../shared/shimmer-effect/shimmer-effect';
import { FormsModule, NgForm } from '@angular/forms';
import { ApiEndpoints } from '../../../core/api-endpoints';
import { UserService } from '../../../services/user';
import { IUser, IUsers } from '../../../interfaces/user';
import { FlashService } from '../../../services/flash';
import { Helpers } from '../../../utilities/helpers';
import { LoadingIndicator } from '../../../shared/loading-indicator/loading-indicator';
import { Session } from '../../../services/session';
import { TeacherService } from '../../../services/teacher';
import { DashboardService } from '../../../services/dashboard';
import { ClassService } from '../../../services/class';
import { AcademicYearService } from '../../../services/academic-year';
import { IAcademicYears } from '../../../interfaces/academic-year';
import { IClasses } from '../../../interfaces/class';
import { ITeacher, ITeachers } from '../../../interfaces/teacher';
import { ILevels } from '../../../interfaces/level';
import { LevelService } from '../../../services/level';

export interface IQualification {
  id: string;
  name: string;
}
@Component({
  selector: 'app-class',
  imports: [
    RouterOutlet,
    ShimmerEffect,
    RouterLink,
    RouterLinkActive,
    FormsModule,
    LoadingIndicator,
  ],
  templateUrl: './class.html',
  styleUrl: './class.css',
})
export class Class {
  constructor(
    protected router: Router,
    protected userService: UserService,
    private session: Session,
    private teacherService: TeacherService,
    private dashboardService: DashboardService,
    private classService: ClassService,
    protected academicYearService: AcademicYearService,
    private levelService: LevelService,
  ) {
    const token = this.session.getItem('token');

    this.loggedUserRole = this.session.decodeToken(token)?.role;
    this.schoolId = this.session.decodeToken(token)?.school_id;

    if (this.loggedUserRole !== 'SUPER_ADMIN') {
      this.getDashboardData();
    }

    this.getAcademicYears();
    this.getLevels();
  }

  protected academicYears = signal<IAcademicYears | null>(null);
  protected isLoading = signal<boolean>(false);
  protected setAddTeacher = signal<boolean>(false);
  protected setAddClass = signal<boolean>(false);
  protected staffNumber: string = '';
  protected hireDate: string = '';
  protected specialization: string = '';
  protected qualification: string = '';
  protected phone: string = '';
  protected employmentType: string = '';
  protected users = signal<IUsers | null>(null);
  protected readonly flashService = inject(FlashService);
  protected query!: string;
  protected userId!: string;
  protected username!: string;
  protected userName: string = '';
  protected allowedEmploymentTypes = ['FULL_TIME', 'PART_TIME', 'CONTRACT', 'TEMPORARY'];
  protected isSubmitting = signal<boolean>(false);
  protected qualifications: IQualification[] = [];
  protected schoolId?: string;
  protected loggedUserRole?: string;
  protected readonly Helpers = Helpers;
  protected dashboardData = signal<any>(null);
  protected isCreatingClass = signal<boolean>(false);
  protected academicYearId: string = '';
  protected levelId: string = '';
  protected nameOfClass: string = '';
  protected classTeacherId: string = '';
  protected capacity!: number;
  protected classes = signal<IClasses | null>(null);
  protected classList = ['A', 'B', 'C', 'D', 'E'];
  protected teachers = signal<ITeachers | null>(null);
  protected teacherName!: string;
  protected teacherPublicId!: string;
  protected levels = signal<ILevels | null>(null);
  protected admins = ['SUPER_ADMIN', 'SCHOOL_ADMIN'];
  protected invalidStatuses = ['INACTIVE', 'COMPLETED', 'ARCHIVED', 'DEACTIVATED', 'SUSPENDED'];

  protected selectTeacher(teacher: ITeacher['payload']) {
    this.query = '';
    this.teacherName = Helpers.fullName({
      first_name: teacher.first_name,
      last_name: teacher.last_name,
    });
    this.teacherPublicId = teacher.public_id;
    this.classTeacherId = teacher.id;
  }

  protected isCurrentRoute(route: string): boolean {
    return this.router.url === route;
  }

  protected existingClasses: {
    name: string;
    level_id: string;
    academic_year_id: string;
  }[] = [];

  protected getLevels(): void {
    const params: Record<string, string | number> = {
      page: 0,
      size: 50,
    };

    this.levelService.getLevels(`${ApiEndpoints.url}/level`, params).subscribe({
      next: (res) => {
        this.levels.set(res);
      },
      error: (err) => {
        this.flashService.failure(err?.error?.message ?? 'Could not load levels. Refresh the page');
      },
    });
  }

  protected getUsers(key: string): void {
    const params = {
      search: key,
      role: 'TEACHER',
    };

    this.userService.getUsers(`${ApiEndpoints.url}/user`, params).subscribe({
      next: (data) => {
        this.users.set(data);
      },
      error: (err) => {
        this.flashService.failure(err?.error?.message ?? 'Could not load users');
      },
    });
  }

  protected getAcademicYears(): void {
    const params: Record<string, string | number> = {
      page: 0,
      size: 30,
    };
    this.academicYearService
      .getAcademicYears(`${ApiEndpoints.url}/academic-year`, params)
      .subscribe({
        next: (res) => {
          this.academicYears.set(res);
        },
        error: (err) => {
          this.flashService.failure(
            err?.error?.message ?? 'Could not load Academic Years. Refresh the page.',
          );
        },
      });
  }

  protected getClasses(): void {
    const params: Record<string, string | number> = {
      page: 0,
      size: 30,
      level_id: this.levelId,
    };

    this.classService.getClasses(`${ApiEndpoints.url}/class`, params).subscribe({
      next: (res) => {
        this.classes.set(res);
        this.existingClasses = res.payload.map((item: any) => ({
          name: item.name,
          level_id: item.level_id,
          academic_year_id: item.academic_year_id,
        }));
      },
      error: (err) => {
        this.flashService.failure(
          err?.error?.message ?? 'Could not load classes. Refresh the page',
        );
      },
    });
  }

  protected getTeachers(query: string): void {
    this.teacherService.getTeachers(`${ApiEndpoints.url}/teacher`, { search: query }).subscribe({
      next: (res) => {
        this.teachers.set(res);
        this.teacherName = '';
      },
      error: (err) => {
        this.flashService.failure(
          err?.error?.message ?? 'Could not load teachers. Refresh the page.',
        );
      },
    });
  }

  protected classExists(className: string): boolean {
    if (!this.levelId || !this.academicYearId) {
      return false;
    }

    return this.existingClasses.some(
      (item) =>
        item.name === className &&
        item.level_id === this.levelId &&
        item.academic_year_id === this.academicYearId,
    );
  }

  protected selectUser(user: IUser['payload']) {
    this.query = '';
    this.userId = user.id;
    this.username = user.username;
    this.userName = Helpers.fullName({ first_name: user.first_name, last_name: user.last_name });
  }

  protected addQualification(): void {
    if (this.qualification.trim() === '') {
      this.flashService.failure('Please add a qualification');
      return;
    }

    const exists = this.qualifications.some(
      (qualification) => qualification.name === this.qualification,
    );

    if (exists) {
      this.flashService.failure('Cannot enter duplicate qualification');
      return;
    }

    const qualificationForm: IQualification = {
      id: crypto.randomUUID(),
      name: this.qualification,
    };

    if (this.qualification != '') {
      this.qualifications.push(qualificationForm);
    } else {
      this.flashService.failure('Please enter a qualification');
    }

    this.qualification = '';
  }

  protected deleteQualification(id: string): void {
    this.qualifications = this.qualifications.filter((i) => i.id !== id);
  }

  protected addTeacher(form: NgForm): void {
    if (form.invalid) {
      this.flashService.failure('Fill all required fields');
      return;
    }

    if (!this.schoolId && this.loggedUserRole === 'SUPER_ADMIN') {
      this.flashService.failure('School ID required');
      return;
    }

    if (!this.schoolId) {
      this.flashService.failure('Error creating level. Try again.');
    }

    this.isSubmitting.set(true);

    let qualifications = [];

    for (let q of this.qualifications) {
      qualifications.push(q.name);
    }

    const addTeacherForm = {
      school_id: this.schoolId,
      user_id: this.userId,
      staff_number: this.staffNumber,
      specialization: this.specialization,
      phone: this.phone,
      employment_type: this.employmentType,
      hire_date: this.hireDate,
      qualification: qualifications,
    };

    this.teacherService.createTeacher(`${ApiEndpoints.url}/teacher`, addTeacherForm).subscribe({
      next: () => {
        this.isSubmitting.set(false);
        this.flashService.success('Teacher added successfully');
        window.location.reload();
      },
      error: (err) => {
        this.isSubmitting.set(false);
        this.flashService.failure(err?.error?.message ?? 'Error creating teacher. Try again.');
      },
    });
  }

  protected createClass(form: NgForm): void {
    if (form.invalid) {
      this.flashService.failure('Fill all required fields');
      return;
    }

    if (!this.schoolId && this.loggedUserRole === 'SUPER_ADMIN') {
      this.flashService.failure('School ID required');
      return;
    }

    if (!this.schoolId) {
      this.flashService.failure('Error creating class. Try again.');
      return;
    }

    this.isCreatingClass.set(true);

    const newClassForm = {
      school_id: this.schoolId,
      academic_year_id: this.academicYearId,
      level_id: this.levelId,
      name: this.nameOfClass,
      class: this.classTeacherId,
      capacity: this.capacity,
      class_teacher_id: this.classTeacherId,
    };

    this.classService.createClass(`${ApiEndpoints.url}/class`, newClassForm).subscribe({
      next: () => {
        this.classTeacherId = '';
        this.setAddClass.set(false);
        this.flashService.success('Class created successfully.');
        this.getDashboardData();
        this.isCreatingClass.set(false);
        window.location.reload();
      },
      error: (err) => {
        this.isCreatingClass.set(false);
        this.flashService.failure(err?.error?.message ?? 'Error creating class. Try again.');
      },
    });
  }

  protected getDashboardData(): void {
    this.isLoading.set(true);

    this.dashboardService
      .getDashboardData(`${ApiEndpoints.url}/dashboard/class-teacher-stats`)
      .subscribe({
        next: (res) => {
          this.dashboardData.set(res.payload);
          this.isLoading.set(false);
        },
        error: (err) => {
          this.flashService.failure(
            err?.error?.message ?? 'Failed to load dashboard. Refresh the page.',
          );
        },
      });
  }
}
